import React, { useState, useEffect } from 'react';
import { Calendar, TrendingUp, TrendingDown, DollarSign, Image as ImageIcon, Loader, RefreshCw, Filter, ChevronDown } from 'lucide-react';
import { format } from 'date-fns';
import { parseCSV } from '../utils/csvParser';

function TradeJournal() {
  const [journalEntries, setJournalEntries] = useState([]);
  const [filteredEntries, setFilteredEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [showProofImage, setShowProofImage] = useState(null);
  const [error, setError] = useState(null);
  const [selectedYear, setSelectedYear] = useState('all');
  const [selectedMonth, setSelectedMonth] = useState('all');
  const [availableYears, setAvailableYears] = useState([]);

  useEffect(() => {
    fetchJournalData();
  }, []);

  const fetchJournalData = async () => {
    setLoading(true);
    setError(null);
    try {
      // Fetch CSV file from public folder
      const response = await fetch('/trading-journal.csv');
      if (!response.ok) {
        throw new Error('Failed to load trading journal data');
      }
      const csvText = await response.text();
      const parsedData = parseCSV(csvText);
      
      // Sort by date in descending order (most recent first)
      const sortedData = parsedData.sort((a, b) => 
        new Date(b.date) - new Date(a.date)
      );
      
      setJournalEntries(sortedData);
      setFilteredEntries(sortedData);
      
      // Extract unique years from data
      const years = [...new Set(sortedData.map(entry => new Date(entry.date).getFullYear()))];
      setAvailableYears(years.sort((a, b) => b - a));
    } catch (err) {
      console.error('Error loading CSV:', err);
      setError('Failed to load trading journal data. Please ensure trading-journal.csv exists in the public folder.');
      setJournalEntries([]);
    } finally {
      setLoading(false);
    }
  };

  const refreshData = async () => {
    setRefreshing(true);
    await fetchJournalData();
    setRefreshing(false);
  };


  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0
    }).format(value);
  };

  const months = [
    { value: 'all', label: 'All Months' },
    { value: '1', label: 'January' },
    { value: '2', label: 'February' },
    { value: '3', label: 'March' },
    { value: '4', label: 'April' },
    { value: '5', label: 'May' },
    { value: '6', label: 'June' },
    { value: '7', label: 'July' },
    { value: '8', label: 'August' },
    { value: '9', label: 'September' },
    { value: '10', label: 'October' },
    { value: '11', label: 'November' },
    { value: '12', label: 'December' }
  ];

  useEffect(() => {
    filterEntries();
  }, [journalEntries, selectedYear, selectedMonth]);

  const filterEntries = () => {
    let filtered = journalEntries;

    if (selectedYear !== 'all') {
      filtered = filtered.filter(entry => 
        new Date(entry.date).getFullYear() === parseInt(selectedYear)
      );
    }

    if (selectedMonth !== 'all') {
      filtered = filtered.filter(entry => 
        new Date(entry.date).getMonth() + 1 === parseInt(selectedMonth)
      );
    }

    setFilteredEntries(filtered);
  };

  const calculateStats = () => {
    if (filteredEntries.length === 0) return { total: 0, profitable: 0, loss: 0, winRate: 0 };
    
    const stats = filteredEntries.reduce((acc, entry) => {
      acc.total += entry.pnl;
      if (entry.pnl > 0) {
        acc.profitable++;
      } else if (entry.pnl < 0) {
        acc.loss++;
      }
      return acc;
    }, { total: 0, profitable: 0, loss: 0 });
    
    stats.winRate = filteredEntries.length > 0 
      ? ((stats.profitable / filteredEntries.length) * 100).toFixed(1)
      : 0;
    
    return stats;
  };

  const stats = calculateStats();

  return (
    <div className="container">
      <div style={{ marginBottom: '30px' }}>
        <h1 style={{ fontSize: '1.8rem', fontWeight: '600', color: '#333', marginBottom: '8px' }}>Daily Trade Details</h1>
        <p style={{ color: '#667eea', fontSize: '0.95rem', fontStyle: 'italic' }}>
          "Every loss is a lesson, every win is progress"
        </p>
      </div>
      
      <div className="stats-grid" style={{ marginBottom: '30px' }}>
        <div className="stat-card">
          <div style={{ fontSize: '0.85rem', color: '#666' }}>Total P&L</div>
          <div className="stat-value" style={{ fontSize: '2rem' }}>
            {formatCurrency(stats.total)}
          </div>
          <div style={{ marginTop: '10px' }}>
            {stats.total >= 0 ? <TrendingUp size={20} /> : <TrendingDown size={20} />}
          </div>
        </div>
        
        <div className="stat-card" style={{ background: 'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)' }}>
          <div style={{ fontSize: '0.85rem', color: '#666' }}>Profit Days</div>
          <div className="stat-value" style={{ fontSize: '2rem' }}>{stats.profitable}</div>
          <div style={{ fontSize: '0.9rem', marginTop: '10px', color: '#666' }}>Win Rate: {stats.winRate}%</div>
        </div>
        
        <div className="stat-card" style={{ background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)' }}>
          <div style={{ fontSize: '0.85rem', color: '#666' }}>Loss Days</div>
          <div className="stat-value" style={{ fontSize: '2rem' }}>{stats.loss}</div>
          <div style={{ fontSize: '0.9rem', marginTop: '10px', color: '#666' }}>Total Trades: {filteredEntries.length}</div>
        </div>
      </div>

      <div className="card" style={{ marginBottom: '30px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '20px', flexWrap: 'wrap' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <Filter size={20} style={{ color: '#667eea' }} />
            <span style={{ fontSize: '1.1rem', fontWeight: '600', color: '#333' }}>Filter Entries:</span>
          </div>
          
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <label style={{ fontSize: '1rem', color: '#666', fontWeight: '500' }}>Year:</label>
            <div style={{ position: 'relative' }}>
              <select
                value={selectedYear}
                onChange={(e) => setSelectedYear(e.target.value)}
                style={{
                  padding: '8px 30px 8px 12px',
                  fontSize: '0.95rem',
                  border: '2px solid #e0e0e0',
                  borderRadius: '6px',
                  background: 'white',
                  color: '#333',
                  cursor: 'pointer',
                  appearance: 'none',
                  minWidth: '120px'
                }}
              >
                <option value="all">All Years</option>
                {availableYears.map(year => (
                  <option key={year} value={year}>{year}</option>
                ))}
              </select>
              <ChevronDown 
                size={16} 
                style={{
                  position: 'absolute',
                  right: '8px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  pointerEvents: 'none',
                  color: '#666'
                }}
              />
            </div>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <label style={{ fontSize: '1rem', color: '#666', fontWeight: '500' }}>Month:</label>
            <div style={{ position: 'relative' }}>
              <select
                value={selectedMonth}
                onChange={(e) => setSelectedMonth(e.target.value)}
                style={{
                  padding: '8px 30px 8px 12px',
                  fontSize: '0.95rem',
                  border: '2px solid #e0e0e0',
                  borderRadius: '6px',
                  background: 'white',
                  color: '#333',
                  cursor: 'pointer',
                  appearance: 'none',
                  minWidth: '140px'
                }}
              >
                {months.map(month => (
                  <option key={month.value} value={month.value}>{month.label}</option>
                ))}
              </select>
              <ChevronDown 
                size={16} 
                style={{
                  position: 'absolute',
                  right: '8px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  pointerEvents: 'none',
                  color: '#666'
                }}
              />
            </div>
          </div>

          <button
            onClick={() => {
              setSelectedYear('all');
              setSelectedMonth('all');
            }}
            style={{
              padding: '8px 16px',
              fontSize: '0.9rem',
              border: '2px solid #667eea',
              borderRadius: '6px',
              background: 'transparent',
              color: '#667eea',
              cursor: 'pointer',
              fontWeight: '500',
              transition: 'all 0.3s ease'
            }}
            onMouseEnter={(e) => {
              e.target.style.background = '#667eea';
              e.target.style.color = 'white';
            }}
            onMouseLeave={(e) => {
              e.target.style.background = 'transparent';
              e.target.style.color = '#667eea';
            }}
          >
            Clear Filters
          </button>
        </div>
      </div>

      <div style={{ marginBottom: '30px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <span style={{ fontSize: '1rem', color: '#666' }}>
            Data from trading journal
          </span>
          <div style={{ 
            width: '8px', 
            height: '8px', 
            backgroundColor: error ? '#ef4444' : '#10b981', 
            borderRadius: '50%' 
          }}></div>
        </div>
        <button 
          className="button"
          onClick={refreshData}
          disabled={refreshing}
          style={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: '8px',
            opacity: refreshing ? 0.7 : 1 
          }}
        >
          <RefreshCw size={18} style={{ animation: refreshing ? 'spin 1s linear infinite' : 'none' }} />
          {refreshing ? 'Refreshing...' : 'Refresh Data'}
        </button>
      </div>

      <div className="card">
        <h2 style={{ fontSize: '1.2rem', fontWeight: '600', color: '#333', marginBottom: '15px' }}>Trade Entries</h2>
        
        {loading ? (
          <div style={{ 
            display: 'flex', 
            flexDirection: 'column',
            alignItems: 'center', 
            justifyContent: 'center',
            padding: '60px',
            color: '#666' 
          }}>
            <Loader size={40} style={{ animation: 'spin 1s linear infinite', marginBottom: '20px' }} />
            <p>Loading trading data from CSV file...</p>
          </div>
        ) : error ? (
          <p style={{ textAlign: 'center', color: '#ef4444', padding: '40px' }}>
            {error}
          </p>
        ) : filteredEntries.length === 0 ? (
          <p style={{ textAlign: 'center', color: '#999', padding: '40px' }}>
            {journalEntries.length === 0 ? 'No algorithm executions found in the data feed.' : 'No executions found for the selected filters.'}
          </p>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ borderBottom: '2px solid #e0e0e0' }}>
                  <th style={{ padding: '12px', textAlign: 'left', color: '#666' }}>Date</th>
                  <th style={{ padding: '12px', textAlign: 'right', color: '#666' }}>Capital</th>
                  <th style={{ padding: '12px', textAlign: 'right', color: '#666' }}>P&L</th>
                  <th style={{ padding: '12px', textAlign: 'right', color: '#666' }}>%</th>
                  <th style={{ padding: '12px', textAlign: 'left', color: '#666' }}>Trade Notes</th>
                  <th style={{ padding: '12px', textAlign: 'center', color: '#666' }}>Proof</th>
                </tr>
              </thead>
              <tbody>
                {filteredEntries.map((entry) => (
                  <tr key={entry.id} style={{ borderBottom: '1px solid #f0f0f0' }}>
                    <td style={{ padding: '12px', fontSize: '0.95rem' }}>
                      {format(new Date(entry.date), 'dd MMM yyyy')}
                    </td>
                    <td style={{ padding: '12px', textAlign: 'right' }}>
                      {formatCurrency(entry.tradingCapital)}
                    </td>
                    <td style={{ 
                      padding: '12px', 
                      textAlign: 'right',
                      color: entry.pnl >= 0 ? '#10b981' : '#ef4444',
                      fontWeight: '600',
                      fontSize: '0.95rem'
                    }}>
                      {entry.pnl >= 0 ? '+' : '-'}{formatCurrency(Math.abs(entry.pnl))}
                    </td>
                    <td style={{ 
                      padding: '12px', 
                      textAlign: 'right',
                      color: entry.pnl >= 0 ? '#10b981' : '#ef4444',
                      fontSize: '0.95rem'
                    }}>
                      {entry.pnl >= 0 ? '+' : ''}{entry.pnlPercentage}%
                    </td>
                    <td style={{ padding: '12px', maxWidth: '300px' }}>
                      <div style={{ 
                        fontSize: '0.9rem', 
                        color: '#666',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap'
                      }}>
                        {entry.notes || 'No algorithm details'}
                      </div>
                    </td>
                    <td style={{ padding: '12px', textAlign: 'center' }}>
                      {entry.proofImage ? (
                        <button
                          onClick={() => setShowProofImage(`/trade-proofs/${entry.proofImage}`)}
                          style={{
                            background: 'none',
                            border: 'none',
                            cursor: 'pointer',
                            color: '#667eea'
                          }}
                        >
                          <ImageIcon size={20} />
                        </button>
                      ) : (
                        <span style={{ color: '#ccc' }}>-</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {showProofImage && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.8)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
          padding: '20px'
        }}
        onClick={() => setShowProofImage(null)}
        >
          <div style={{ position: 'relative', maxWidth: '90%', maxHeight: '90%' }}>
            <img 
              src={showProofImage} 
              alt="Trade proof"
              style={{ 
                maxWidth: '100%', 
                maxHeight: '90vh',
                borderRadius: '8px'
              }}
            />
            <button
              style={{
                position: 'absolute',
                top: '-40px',
                right: '0',
                background: 'white',
                border: 'none',
                borderRadius: '50%',
                width: '40px',
                height: '40px',
                cursor: 'pointer',
                fontSize: '24px'
              }}
              onClick={() => setShowProofImage(null)}
            >
              ×
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default TradeJournal;